import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiError,
  adminCloseRound,
  adminCloseSubmissions,
  adminCreateRound,
  adminDeleteRound,
  adminExhibitionSubmission,
  adminOpenRound,
  adminReorderRound,
  adminRescore,
  adminBuildPlaylist,
  adminUnbindUser,
  adminUsers,
  adminUnopenRound,
  adminUpdateSettings,
  getRuleset,
  putRuleset,
  getMgmtLeague,
  getStatus,
  putActive,
  getRoster,
  getSeasonRounds,
  spotifyConnect,
  spotifySearch,
  spotifyStatus,
} from '../core/api';
import { SEASON_ID } from '../core/config';
import { useAsync } from '../core/useAsync';
import { useApp } from '../App';
import type { AdminUser } from '../core/api';
import type { AdminRuleset, MediaType, Pace, RosterMember, SeasonRound, SpotifyResult, Style } from '../core/types';
import type { LeagueSettings } from '../core/api';

const PACE_OPTS: { key: Pace; label: string }[] = [
  { key: 'rapid', label: 'Rapid — 24h a phase' },
  { key: 'steady', label: 'Steady — 3 days a phase' },
  { key: 'slow', label: 'Slow — 1 week a phase' },
];

type Tab = 'rounds' | 'seed' | 'people' | 'scoring' | 'settings';

/**
 * The admin console (spec §21). Deliberately not the pixel theme — dense data
 * entry. The one guard that matters is the API's, not this UI's: the exhibition
 * endpoints reject a live round (409 exhibition_only), so seeding can never spoil
 * a real game. This screen is only reachable by an is_admin caller.
 */
export function Admin() {
  const { user } = useApp();
  const nav = useNavigate();
  const [tab, setTab] = useState<Tab>('rounds');

  if (!user.is_admin) {
    return (
      <div className="admin">
        <div className="strip">
          <span className="title">ADMIN</span>
          <a onClick={() => nav('/')}>← back</a>
        </div>
        <div className="body">
          <p className="hint">You are not an administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin">
      <div className="strip">
        <span className="title">THIS PLACE IS A PRISON · ADMIN</span>
        <a onClick={() => nav('/')}>← back to app</a>
      </div>
      <nav className="tabs">
        {(['rounds', 'seed', 'people', 'scoring', 'settings'] as Tab[]).map((tb) => (
          <button key={tb} className={tab === tb ? 'on' : ''} onClick={() => setTab(tb)}>
            {tb === 'seed' ? 'SEED TEST ROUND' : tb.toUpperCase()}
          </button>
        ))}
      </nav>
      <div className="body">
        {tab === 'rounds' && <RoundsTab />}
        {tab === 'seed' && <SeedTab />}
        {tab === 'people' && <PeopleTab />}
        {tab === 'scoring' && <ScoringTab />}
        {tab === 'settings' && <SettingsTab />}
      </div>
    </div>
  );
}

// ---- Rounds --------------------------------------------------------------

function RoundsTab() {
  const rounds = useAsync<SeasonRound[]>(() => getSeasonRounds(SEASON_ID), []);
  const spotify = useAsync<{ connected: boolean }>(spotifyStatus, []);
  const league = useAsync<LeagueSettings>(getMgmtLeague, []);
  const allowedPaces = league.data?.allowed_paces ?? (['rapid', 'steady', 'slow'] as Pace[]);
  const [busy, setBusy] = useState<number | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  async function buildPlaylist(id: number) {
    setBusy(id);
    setMsg(null);
    try {
      const r = await adminBuildPlaylist(id);
      setMsg(`Playlist built (${r.tracks} tracks): ${r.url}`);
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not build the playlist.');
    } finally {
      setBusy(null);
    }
  }

  const [theme, setTheme] = useState('');
  const [note, setNote] = useState('');
  const [mediaType, setMediaType] = useState<MediaType>('audio');
  const [exhibition, setExhibition] = useState(false);
  const [pace, setPace] = useState<Pace>('steady');
  const [creating, setCreating] = useState(false);

  // Default the pace to the league's first allowed pace once settings load.
  useEffect(() => {
    if (league.data && !league.data.allowed_paces.includes(pace)) {
      setPace(league.data.allowed_paces[0] ?? 'steady');
    }
  }, [league.data]); // eslint-disable-line react-hooks/exhaustive-deps

  async function act(id: number, fn: () => Promise<unknown>, label: string, confirmFirst = true) {
    if (confirmFirst && !confirm(`${label}?`)) return;
    setBusy(id);
    setMsg(null);
    try {
      await fn();
      setMsg(`${label} — done.`);
      rounds.reload();
    } catch (e) {
      setMsg((e as ApiError).message || 'Action failed.');
    } finally {
      setBusy(null);
    }
  }

  async function create() {
    if (!theme.trim()) return;
    setCreating(true);
    setMsg(null);
    try {
      const r = await adminCreateRound({
        theme: theme.trim(),
        theme_note: note.trim() || undefined,
        media_type: mediaType,
        is_exhibition: exhibition,
        pace,
      });
      setTheme('');
      setNote('');
      setMediaType('audio');
      setExhibition(false);
      setMsg(
        r.opened
          ? `Round #${r.sequence_no} created and opened for submissions.`
          : `Round #${r.sequence_no} created and scheduled — another round is already open, so open this one when you're ready.`,
      );
      rounds.reload();
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not create round.');
    } finally {
      setCreating(false);
    }
  }

  return (
    <>
      <h2>ROUNDS</h2>
      <p className="hint">Create rounds and move them through their phases. Closing a round scores it and reveals identities.</p>
      {msg ? <div className="status ok">{msg}</div> : null}

      <div className="card">
        <div className="lbl">Spotify playlists</div>
        {spotify.loading ? (
          <p className="hint">Checking connection…</p>
        ) : spotify.data?.connected ? (
          <p className="hint">✓ Connected. A playlist is built automatically when submissions close; “Build playlist” on an audio round refills it.</p>
        ) : (
          <>
            <p className="hint">Not connected. Connect a Spotify account so round playlists can be built on it.</p>
            <button className="btn" onClick={spotifyConnect}>Connect Spotify</button>
          </>
        )}
      </div>

      <div className="card">
        <div className="lbl">Create a round</div>
        <label>Theme</label>
        <input type="text" value={theme} onChange={(e) => setTheme(e.target.value)} placeholder="e.g. One-hit wonders" />
        <label>Theme note (optional)</label>
        <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. One hit. Not two." />
        <label>Round type</label>
        <select value={mediaType} onChange={(e) => setMediaType(e.target.value as MediaType)}>
          <option value="audio">Song (Spotify)</option>
          <option value="image">Image (upload)</option>
          <option value="video">Video (YouTube)</option>
        </select>
        {allowedPaces.length > 1 ? (
          <>
            <label>Pace</label>
            <select value={pace} onChange={(e) => setPace(e.target.value as Pace)}>
              {PACE_OPTS.filter((p) => allowedPaces.includes(p.key)).map((p) => (
                <option key={p.key} value={p.key}>{p.label}</option>
              ))}
            </select>
          </>
        ) : null}
        <label style={{ display: 'flex', gap: 8, alignItems: 'center', textTransform: 'none' }}>
          <input type="checkbox" checked={exhibition} onChange={(e) => setExhibition(e.target.checked)} style={{ width: 'auto' }} />
          Exhibition round (excluded from boards; admin can enter everyone's picks)
        </label>
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" disabled={!theme.trim() || creating} onClick={create}>
            {creating ? '…' : 'CREATE ROUND'}
          </button>
        </div>
      </div>

      {rounds.loading ? (
        <p className="hint">Loading…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>#</th>
              <th>Theme</th>
              <th>Phase</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {(rounds.data ?? []).map((r, i, arr) => (
              <tr key={r.round_id}>
                <td>{r.sequence_no}</td>
                <td>
                  {r.theme}
                  {r.is_exhibition ? <span className="pill EXH" style={{ marginLeft: 8 }}>EXH</span> : null}
                </td>
                <td>
                  <span className={`pill ${r.phase}`}>{r.phase}</span>
                </td>
                <td>
                  <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                    {r.phase === 'SCHEDULED' && (
                      <>
                        <button
                          className="btn sm ghost"
                          title="Move earlier"
                          disabled={busy === r.round_id || !arr.slice(0, i).some((x) => x.phase === 'SCHEDULED')}
                          onClick={() => act(r.round_id, () => adminReorderRound(r.round_id, 'up'), 'Move up', false)}
                        >
                          ↑
                        </button>
                        <button
                          className="btn sm ghost"
                          title="Move later"
                          disabled={busy === r.round_id || !arr.slice(i + 1).some((x) => x.phase === 'SCHEDULED')}
                          onClick={() => act(r.round_id, () => adminReorderRound(r.round_id, 'down'), 'Move down', false)}
                        >
                          ↓
                        </button>
                        <button className="btn sm" disabled={busy === r.round_id} onClick={() => act(r.round_id, () => adminOpenRound(r.round_id), 'Open this round for submissions')}>
                          Open
                        </button>
                        <button className="btn sm ghost warn" disabled={busy === r.round_id} onClick={() => act(r.round_id, () => adminDeleteRound(r.round_id), `Delete "${r.theme}"`)}>
                          Delete
                        </button>
                      </>
                    )}
                    {r.phase === 'SUBMITTING' && (
                      <>
                        <button className="btn sm" disabled={busy === r.round_id} onClick={() => act(r.round_id, () => adminCloseSubmissions(r.round_id), 'Close submissions → open voting')}>
                          Close subs
                        </button>
                        <button className="btn sm ghost" title="Return to scheduled (opened by accident)" disabled={busy === r.round_id} onClick={() => act(r.round_id, () => adminUnopenRound(r.round_id), 'Un-open (back to scheduled)')}>
                          Un-open
                        </button>
                      </>
                    )}
                    {r.phase === 'VOTING' && (
                      <button className="btn sm warn" disabled={busy === r.round_id} onClick={() => act(r.round_id, () => adminCloseRound(r.round_id), 'Close round & score')}>
                        Close & score
                      </button>
                    )}
                    {r.phase === 'CLOSED' && (
                      <button className="btn sm ghost" disabled={busy === r.round_id} onClick={() => act(r.round_id, () => adminRescore(r.round_id), 'Rescore')}>
                        Rescore
                      </button>
                    )}
                    {r.media_type === 'audio' && (r.phase === 'VOTING' || r.phase === 'CLOSED') && spotify.data?.connected && (
                      <button className="btn sm ghost" disabled={busy === r.round_id} onClick={() => buildPlaylist(r.round_id)}>
                        Build playlist
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// ---- Seed a test (exhibition) round --------------------------------------

function SeedTab() {
  const roster = useAsync<RosterMember[]>(getRoster, []);
  const [roundId, setRoundId] = useState<number | null>(null);
  const [theme, setTheme] = useState('Unprotected Sax');
  const [note, setNote] = useState('Songs featuring a saxophone');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [assigned, setAssigned] = useState<Record<number, string>>({});
  const [opened, setOpened] = useState(false);

  async function createRound() {
    setBusy(true);
    setMsg(null);
    try {
      const r = await adminCreateRound({ theme: theme.trim(), theme_note: note.trim() || undefined, is_exhibition: true });
      setRoundId(r.round_id);
      setMsg(`Exhibition round #${r.sequence_no} created — now assign a song to each player.`);
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not create round.');
    } finally {
      setBusy(false);
    }
  }

  async function assign(userId: number, song: SpotifyResult) {
    if (roundId == null) return;
    try {
      await adminExhibitionSubmission(roundId, userId, song.media_ref);
      setAssigned((prev) => ({ ...prev, [userId]: `${song.title} — ${song.subtitle}` }));
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not assign that song.');
    }
  }

  async function openVoting() {
    if (roundId == null) return;
    setBusy(true);
    setMsg(null);
    try {
      await adminCloseSubmissions(roundId);
      setOpened(true);
      setMsg('Voting is open — your friends can now sign in and vote on these tracks.');
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not open voting.');
    } finally {
      setBusy(false);
    }
  }

  const players = roster.data ?? [];
  const assignedCount = Object.keys(assigned).length;

  return (
    <>
      <h2>SEED A TEST ROUND</h2>
      <p className="hint">
        Load a real round's submissions as an <b>exhibition round</b> so everyone can jump straight into voting. This
        can't touch a live game — the API only lets an admin enter picks on exhibition rounds.
      </p>
      {msg ? <div className={`status ${msg.startsWith('Voting is open') ? 'ok' : 'ok'}`}>{msg}</div> : null}

      {roundId == null ? (
        <div className="card">
          <div className="lbl">1 · Create the round</div>
          <label>Theme</label>
          <input type="text" value={theme} onChange={(e) => setTheme(e.target.value)} />
          <label>Note</label>
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} />
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" disabled={!theme.trim() || busy} onClick={createRound}>
              {busy ? '…' : 'CREATE EXHIBITION ROUND'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="card">
            <div className="lbl">2 · Assign each player's song ({assignedCount}/{players.length})</div>
            <table>
              <thead>
                <tr>
                  <th>Player</th>
                  <th>Song</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p) => (
                  <tr key={p.user_id}>
                    <td style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{p.display_name}</td>
                    <td>
                      {assigned[p.user_id] ? (
                        <span className="seed-done">✓ {assigned[p.user_id]}</span>
                      ) : opened ? (
                        <span className="hint">—</span>
                      ) : (
                        <SongPicker onPick={(song) => assign(p.user_id, song)} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <div className="lbl">3 · Open voting</div>
            <p className="hint">Once you've assigned everyone (or as many as you want), open voting. Order is randomised and duplicates flagged automatically.</p>
            <button className="btn" disabled={busy || opened || assignedCount < 2} onClick={openVoting}>
              {opened ? '✓ VOTING OPEN' : busy ? '…' : 'CLOSE SUBMISSIONS → OPEN VOTING'}
            </button>
          </div>
        </>
      )}
    </>
  );
}

function SongPicker({ onPick }: { onPick: (song: SpotifyResult) => Promise<void> }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState<SpotifyResult[]>([]);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    window.clearTimeout(timer.current);
    const v = q.trim();
    if (!v) {
      setRows([]);
      return;
    }
    timer.current = window.setTimeout(async () => {
      try {
        setRows(await spotifySearch(v));
      } catch {
        setRows([]);
      }
    }, 300);
    return () => window.clearTimeout(timer.current);
  }, [q]);

  return (
    <div>
      <input type="text" placeholder="search Spotify…" value={q} onChange={(e) => setQ(e.target.value)} />
      {rows.length ? (
        <div className="results">
          {rows.map((r) => (
            <button
              key={r.media_ref}
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                await onPick(r);
                setBusy(false);
                setQ('');
                setRows([]);
              }}
            >
              {r.title} <span className="sub">· {r.subtitle}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

// ---- People --------------------------------------------------------------

function PeopleTab() {
  const users = useAsync<AdminUser[]>(adminUsers, []);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  async function unbind(userId: number, name: string) {
    if (!confirm(`Unbind ${name}? Their claim is released and they re-claim on next sign-in.`)) return;
    setBusy(userId);
    setMsg(null);
    try {
      await adminUnbindUser(userId);
      setMsg(`${name} unbound — they'll re-claim on next sign-in.`);
      users.reload();
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not unbind.');
    } finally {
      setBusy(null);
    }
  }

  const rows = users.data ?? [];
  const claimed = rows.filter((u) => u.claimed).length;

  return (
    <>
      <h2>PEOPLE</h2>
      <p className="hint">
        {claimed} of {rows.length} profiles claimed. Unbind a <b>claimed</b> profile to free it for
        re-claiming (e.g. someone grabbed the wrong one). Unclaimed profiles are just waiting for
        their owner's first sign-in — there's nothing to unbind.
      </p>
      {msg ? <div className="status ok">{msg}</div> : null}
      {users.loading ? (
        <p className="hint">Loading…</p>
      ) : (
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.user_id}>
                <td style={{ fontWeight: 600 }}>
                  {p.display_name}
                  {p.is_admin ? <span className="pill" style={{ marginLeft: 8 }}>ADMIN</span> : null}
                </td>
                <td>
                  {p.claimed ? (
                    <>
                      <span className="pill CLOSED">CLAIMED</span>
                      {p.claimed_at ? (
                        <div className="hint" style={{ marginTop: 4 }}>
                          {new Date(p.claimed_at).toLocaleString(undefined, {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      ) : null}
                    </>
                  ) : (
                    <span className="hint">not claimed yet</span>
                  )}
                </td>
                <td>
                  {p.claimed ? (
                    <button className="btn sm ghost" disabled={busy === p.user_id} onClick={() => unbind(p.user_id, p.display_name)}>
                      Unbind
                    </button>
                  ) : (
                    <span className="hint">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}

// ---- Settings ------------------------------------------------------------

const RULESET_FIELDS: { key: keyof AdminRuleset; label: string; hint?: string }[] = [
  { key: 'budget', label: 'Points budget', hint: 'Total points each player spends across a round.' },
  { key: 'track_min', label: 'Minimum per track', hint: 'Set to 0 to switch off negative voting (marking a track down).' },
  { key: 'track_max', label: 'Maximum per track' },
  { key: 'negative_multiplier', label: 'Down-vote cost multiplier', hint: 'How much a mark-down costs against the budget. Ignored once the minimum is 0.' },
  { key: 'veto_threshold', label: 'Veto threshold', hint: 'How many players must veto the same track for it to land. Applies to the current open round too.' },
  { key: 'veto_penalty', label: 'Veto penalty' },
  { key: 'popularity_bonus', label: 'Most-voters bonus' },
  { key: 'popularity_tie_bonus', label: 'Most-voters tie bonus' },
];

function ScoringTab() {
  const load = useAsync<AdminRuleset>(getRuleset, []);
  const [form, setForm] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [ok, setOk] = useState(false);
  // null = the backend doesn't expose veto_enabled yet, so hide the toggle.
  const [veto, setVeto] = useState<boolean | null>(null);

  useEffect(() => {
    if (!load.data) return;
    const f: Record<string, string> = {};
    for (const { key } of RULESET_FIELDS) f[key] = String(load.data[key]);
    setForm(f);
    setVeto(typeof load.data.veto_enabled === 'boolean' ? load.data.veto_enabled : null);
  }, [load.data]);

  async function save() {
    const payload: Record<string, number | boolean> = {};
    for (const { key, label } of RULESET_FIELDS) {
      const raw = (form[key] ?? '').trim();
      const n = Number(raw);
      if (raw === '' || !Number.isInteger(n)) {
        setOk(false);
        setMsg(`${label} must be a whole number.`);
        return;
      }
      payload[key] = n;
    }
    if (veto !== null) payload.veto_enabled = veto;
    setBusy(true);
    setMsg(null);
    try {
      const next = await putRuleset(payload as Partial<AdminRuleset>);
      const f: Record<string, string> = {};
      for (const { key } of RULESET_FIELDS) f[key] = String(next[key]);
      setForm(f);
      setVeto(typeof next.veto_enabled === 'boolean' ? next.veto_enabled : null);
      setOk(true);
      setMsg('Scoring updated. Live on the open round and every future round.');
    } catch (e) {
      setOk(false);
      setMsg((e as ApiError).message || 'Could not update scoring.');
    } finally {
      setBusy(false);
    }
  }

  if (load.loading) return <p className="hint">Loading…</p>;
  if (load.error) return <div className="status err">Could not load the scoring ruleset.</div>;

  return (
    <>
      <h2>SCORING</h2>
      <p className="hint">
        The season's scoring rules. Changes apply to the current open round and all future rounds — closed rounds are never re-scored.
      </p>
      {msg ? <div className={`status ${ok ? 'ok' : 'err'}`}>{msg}</div> : null}
      <div className="card">
        {veto !== null ? (
          <div style={{ marginBottom: 16, paddingBottom: 14, borderBottom: '1px solid var(--line)' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="checkbox" checked={veto} onChange={(e) => setVeto(e.target.checked)} style={{ width: 'auto' }} />
              Veto enabled
            </label>
            <p className="hint" style={{ marginTop: 4 }}>
              Off = no veto at all: the Veto screen and the RPG veto spot disappear, and no veto is needed to close voting. Applies to the current open round and future rounds.
            </p>
          </div>
        ) : null}
        {RULESET_FIELDS.map(({ key, label, hint }) => (
          <div key={key}>
            <label>{label}</label>
            <input
              type="number"
              step={1}
              value={form[key] ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
            />
            {hint ? (
              <p className="hint" style={{ marginTop: 4 }}>
                {hint}
              </p>
            ) : null}
          </div>
        ))}
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" disabled={busy} onClick={save}>
            {busy ? '…' : 'SAVE SCORING'}
          </button>
        </div>
      </div>
    </>
  );
}

function GameStatusCard() {
  const load = useAsync<{ active: boolean }>(getStatus, []);
  const [active, setActive] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  useEffect(() => {
    if (load.data) setActive(load.data.active);
  }, [load.data]);

  async function setTo(next: boolean) {
    if (!next && !window.confirm('Pause the league? Everyone will see the holding page until you turn this back on.')) return;
    setBusy(true);
    setMsg(null);
    try {
      const res = await putActive(next);
      setActive(res.active);
      setOk(true);
      setMsg(res.active ? 'Game on — players see the league.' : 'Paused — players see the holding page.');
    } catch (e) {
      setOk(false);
      setMsg((e as ApiError).message || 'Could not change the game status.');
    } finally {
      setBusy(false);
    }
  }

  // Hide entirely until the backend exposes /api/status — feature-detected.
  if (load.loading || load.error || active === null) return null;

  return (
    <>
      <h2>GAME STATUS</h2>
      <p className="hint">Turn the whole league on or off. Paused shows every visitor the holding page at /.</p>
      {msg ? <div className={`status ${ok ? 'ok' : 'err'}`}>{msg}</div> : null}
      <div className="card">
        <p style={{ fontWeight: 700, marginBottom: 12 }}>
          {active ? 'Currently: game on — players see the league.' : 'Currently: paused — players see the holding page.'}
        </p>
        <div className="row">
          {active ? (
            <button className="btn warn" disabled={busy} onClick={() => setTo(false)}>
              {busy ? '…' : 'Pause the league'}
            </button>
          ) : (
            <button className="btn" disabled={busy} onClick={() => setTo(true)}>
              {busy ? '…' : 'Turn the game on'}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

function SettingsTab() {
  const [joinCode, setJoinCode] = useState('');
  const [style, setStyle] = useState<Style | ''>('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  async function save() {
    const payload: { join_code?: string; house_style?: Style } = {};
    if (joinCode.trim()) payload.join_code = joinCode.trim();
    if (style) payload.house_style = style;
    if (!payload.join_code && !payload.house_style) return;
    setBusy(true);
    setMsg(null);
    try {
      await adminUpdateSettings(payload);
      setMsg('Settings updated.');
      setJoinCode('');
    } catch (e) {
      setMsg((e as ApiError).message || 'Could not update settings.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <GameStatusCard />
      <h2>SETTINGS</h2>
      <p className="hint">Rotate the join code, or set the season's house style.</p>
      {msg ? <div className="status ok">{msg}</div> : null}
      <div className="card">
        <label>New join code (leave blank to keep)</label>
        <input type="text" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="e.g. BIDET" />
        <label>House style</label>
        <select value={style} onChange={(e) => setStyle(e.target.value as Style)}>
          <option value="">(leave unchanged)</option>
          <option value="simple">Simple</option>
          <option value="prison">Prison</option>
          <option value="abc">Memory (ABC)</option>
        </select>
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" disabled={busy} onClick={save}>
            {busy ? '…' : 'SAVE'}
          </button>
        </div>
      </div>
    </>
  );
}
